import React, { useState, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  Stack,
  Paper,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { getErrorMessage } from '../utils/errorMessages';

const SCREENSHOT_MAX_BYTES = 10 * 1024 * 1024; // 10MB
const ACCEPT_IMAGES = 'image/png,image/jpeg,image/jpg,image/gif,image/webp';

const AgentSupport: React.FC = () => {
  const { agencySlug } = useParams<{ agencySlug: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotError, setScreenshotError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleScreenshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScreenshotError(null);
    const f = e.target.files?.[0];
    if (!f) {
      setScreenshot(null);
      return;
    }
    if (f.size > SCREENSHOT_MAX_BYTES) {
      setScreenshotError('Screenshot must not exceed 10MB.');
      setScreenshot(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowed.includes(f.type)) {
      setScreenshotError('Screenshot must be PNG, JPEG, GIF, or WebP.');
      setScreenshot(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setScreenshot(f);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setSuccess(null);
    setError(null);
    setScreenshotError(null);

    if (!subject.trim() || !message.trim()) {
      setError('Please fill in subject and description.');
      setSubmitting(false);
      return;
    }

    if (message.trim().length < 10) {
      setError('Please provide more details about the issue (at least 10 characters).');
      setSubmitting(false);
      return;
    }

    if (screenshot && screenshot.size > SCREENSHOT_MAX_BYTES) {
      setScreenshotError('Screenshot must not exceed 10MB.');
      setSubmitting(false);
      return;
    }

    if (!agencySlug) {
      setError('Agency context missing.');
      setSubmitting(false);
      return;
    }

    try {
      if (screenshot) {
        const form = new FormData();
        form.append('subject', subject.trim());
        form.append('message', message.trim());
        form.append('screenshot', screenshot);
        await api.post(`/agencies/${agencySlug}/support`, form, {
          headers: { 'Content-Type': undefined },
        });
      } else {
        await api.post(`/agencies/${agencySlug}/support`, {
          subject: subject.trim(),
          message: message.trim(),
        });
      }

      setSuccess('Your support request has been submitted. We will get back to you as soon as possible.');
      setSubject('');
      setMessage('');
      setScreenshot(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Support
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Report an issue or request help. We’ll respond as soon as we can.
        </Typography>
      </Box>

      <Card>
        <CardContent sx={{ p: 4 }}>
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <TextField
                label="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
                fullWidth
                inputProps={{ maxLength: 100 }}
                helperText={`${subject.length}/100 characters`}
              />

              <TextField
                label="Description"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                fullWidth
                multiline
                rows={6}
                inputProps={{ maxLength: 2000 }}
                helperText={`${message.length}/2000 characters`}
                placeholder="Describe the issue you're experiencing..."
              />

              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Screenshot (optional, max 10MB)
                </Typography>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT_IMAGES}
                  onChange={handleScreenshotChange}
                  style={{ display: 'block', marginBottom: 4 }}
                />
                {screenshot && (
                  <Typography variant="caption" color="text.secondary">
                    Selected: {screenshot.name} ({(screenshot.size / 1024).toFixed(1)} KB)
                  </Typography>
                )}
                {screenshotError && (
                  <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                    {screenshotError}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/agencies/${agencySlug}/dashboard`)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting}
                  sx={{ minWidth: 120 }}
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </Button>
              </Box>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Paper sx={{ mt: 3, p: 3, backgroundColor: '#f0f8ff', border: '1px solid #d0e7ff' }}>
        <Typography variant="body2" color="text.secondary" align="center">
          <strong>Urgent?</strong> Email us at{' '}
          <a href="mailto:support@workflow.estate" style={{ color: '#007AFF' }}>
            support@workflow.estate
          </a>
        </Typography>
      </Paper>
    </Container>
  );
};

export default AgentSupport;
